import { useCallback } from 'react';

import { CLERK_ERROR_CODE } from '@/features/auth/shared/consts/clerk';
import type { AuthError } from '@/features/auth/shared/types/auth';
import { mapClerkError } from '@/features/auth/shared/utils/clerkErrorMapper';
import { createFinalizeNavigate } from '@/features/auth/shared/utils/createFinalizeNavigate';
import { extractClerkErrorCode } from '@/features/auth/shared/utils/extractClerkError';
import { useClerk, useSignIn } from '@clerk/react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';

export const useCredentialsLogin = (
  setError: (error: AuthError | null) => void,
  setLoading: (loading: boolean) => void
) => {
  const { signIn } = useSignIn();
  const clerk = useClerk();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const submitEmail = useCallback(
    async (email: string) => {
      if (!clerk.loaded) {
        return false;
      }

      setLoading(true);
      setError(null);

      try {
        const { error } = await signIn.create({ identifier: email });

        if (error) {
          const errorCode = extractClerkErrorCode(error);

          if (
            errorCode === CLERK_ERROR_CODE.IDENTIFIER_ALREADY_SIGNED_IN ||
            errorCode === CLERK_ERROR_CODE.SESSION_EXISTS
          ) {
            toast.success(t('success.auth.sessionExists'));
            void navigate('/');
            return false;
          }

          setError(mapClerkError(error, t));
          return false;
        }

        if (signIn.status === 'complete' && signIn.createdSessionId) {
          await signIn.finalize({ navigate: createFinalizeNavigate(navigate) });
          toast.success(t('success.auth.sessionExists'));
          return true;
        }

        const hasPasswordFactor = signIn.supportedFirstFactors.some((f) => f.strategy === 'password');
        if (!hasPasswordFactor) {
          setError({ type: 'no_password', message: t('auth.login.noPasswordSet') });
        }

        return true;
      } catch (err) {
        setError(mapClerkError(err, t));
        return false;
      } finally {
        setLoading(false);
      }
    },
    [clerk.loaded, signIn, navigate, t, setError, setLoading]
  );

  const submitPassword = useCallback(
    async (email: string, password: string): Promise<void> => {
      if (!clerk.loaded) {
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const { error } = await signIn.password({ password, identifier: email });

        if (error) {
          setError(mapClerkError(error, t));
          return;
        }

        if (signIn.status === 'complete') {
          await signIn.finalize({ navigate: createFinalizeNavigate(navigate) });
          return;
        }

        setError({ type: 'password', message: t('error.login.unknown') });
      } catch (err) {
        setError(mapClerkError(err, t));
      } finally {
        setLoading(false);
      }
    },
    [clerk.loaded, signIn, navigate, t, setError, setLoading]
  );

  return { submitEmail, submitPassword };
};
